import { useCallback, useEffect, useRef, useState, useContext } from 'react';
import { useQuery, QueryClientContext } from '@tanstack/react-query';
import { api, authToken, API_BASE } from '../lib/api-client';
import type { CanvasSignal, ChatCard, ChatMessage, ChatSseEvent, ChatThreadSummary, EntityType, CommitDescriptor, TraceStep, TurnTrace, TurnMeta } from '../types/chat';

const HISTORY_CAP = 20;
const THREAD_STORAGE_KEY = 'friday_chat_thread';
const UNDOABLE_ENTITY_TYPES = new Set<EntityType>(['contact', 'talent']);

const LINK_PREFIX: Partial<Record<EntityType, string>> = {
  contact: '/contacts',
  opportunity: '/opportunities',
  talent: '/talent',
  campaign: '/campaigns',
  draft: '/draft',
};

function linkFor(entityType: EntityType, id: string | undefined): string | undefined {
  const p = LINK_PREFIX[entityType];
  return p && id ? `${p}/${id}` : undefined;
}

function nowSecs(): number {
  return Math.floor(Date.now() / 1000);
}

// Hook no longer accepts a threadId prop — it owns state internally.
export function useChat() {
  const [threadId, setThreadId] = useState<string>(() => crypto.randomUUID());
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [streamingText, setStreamingText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Last draft chat touched. Deliberately NOT stored on the message — the canvas
  // is a live surface, and replaying "open the draft" every time an old thread is
  // reloaded would yank the user out of whatever they were doing.
  const [canvasSignal, setCanvasSignal] = useState<CanvasSignal | null>(null);
  // Live numbers for the metrics bar. Set from the `meta` frame at the very start of
  // a turn and topped up as trace steps and `done` arrive, so the thread id is on
  // screen from the first moment rather than only once a turn succeeds.
  const [turnMeta, setTurnMeta] = useState<TurnMeta | null>(null);

  const idRef = useRef(0);
  const nextId = () => `local-${++idRef.current}`;

  // AbortController ref — fresh on each send(), used by stop()
  const abortRef = useRef<AbortController | null>(null);

  // useQueryClient throws if there's no provider — use context check to make it optional.
  const queryClientCtx = useContext(QueryClientContext);
  const queryClient = queryClientCtx ?? null;

  // The user's saved model pick (populated by the metrics-bar picker / Settings).
  // Sent per-request so a just-changed pick applies to the in-flight turn; the
  // server re-validates and holds the saved selection anyway, so a cold cache
  // just means the server's own resolution applies.
  const modelSelection = (): { provider?: string; model?: string } => {
    const my = queryClient?.getQueryData<{ selection: { provider: string; model: string } | null }>(
      ['ai-settings', 'me'],
    );
    return my?.selection ? { provider: my.selection.provider, model: my.selection.model } : {};
  };

  // --- Restore last conversation on mount ---
  useEffect(() => {
    const storedId = window.localStorage.getItem(THREAD_STORAGE_KEY);
    if (!storedId) return;

    api
      .get<{
        thread: { id: string; title: string };
        messages: Array<{ id: string; role: string; content: string; cards?: ChatCard[]; trace?: TurnTrace; created_at?: number }>;
      }>('/chat/threads/' + storedId)
      .then((data) => {
        const mapped: ChatMessage[] = data.messages.map((m) => ({
          id: m.id,
          role: m.role as 'user' | 'assistant',
          content: m.content,
          cards: m.cards ?? [],
          // Left undefined when the stored row has none. A message from before the
          // trace shipped must not render as an answer that used no sources.
          trace: m.trace,
          created_at: m.created_at,
        }));
        setMessages(mapped);
        setThreadId(storedId);
      })
      .catch(() => {
        // Thread not found or load failed — fall back to fresh chat
        window.localStorage.removeItem(THREAD_STORAGE_KEY);
      });
  // Run once on mount only
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist threadId to localStorage whenever it changes
  useEffect(() => {
    window.localStorage.setItem(THREAD_STORAGE_KEY, threadId);
  }, [threadId]);

  // --- newChat ---
  const newChat = useCallback(() => {
    // Fix 3: Abort any in-flight send so the old stream can't re-populate the reset conversation.
    abortRef.current?.abort();
    setIsSending(false);
    setMessages([]);
    setError(null);
    setStreamingText('');
    // The numbers describe the turn you just left, so they go with it.
    setTurnMeta(null);
    const freshId = crypto.randomUUID();
    setThreadId(freshId);
    window.localStorage.setItem(THREAD_STORAGE_KEY, freshId);
  }, []);

  // --- loadThread ---
  const loadThread = useCallback(async (id: string) => {
    // Abort any in-flight send and clear the sending flag.
    abortRef.current?.abort();
    setIsSending(false);

    setError(null);
    setStreamingText('');
    setTurnMeta(null);

    const data = await api.get<{
      thread: { id: string; title: string };
      messages: Array<{ id: string; role: string; content: string; cards?: ChatCard[]; trace?: TurnTrace; created_at?: number }>;
    }>('/chat/threads/' + id);

    const mapped: ChatMessage[] = data.messages.map((m) => ({
      id: m.id,
      role: m.role as 'user' | 'assistant',
      content: m.content,
      cards: m.cards ?? [],
      trace: m.trace,
      created_at: m.created_at,
    }));

    setMessages(mapped);
    setThreadId(id);
    window.localStorage.setItem(THREAD_STORAGE_KEY, id);
  }, []);

  // --- deleteThread ---
  const deleteThread = useCallback(async (id: string) => {
    await api.delete('/chat/threads/' + id);
    queryClient?.invalidateQueries({ queryKey: ['chat-threads'] });
  }, [queryClient]);

  // --- rename ---
  const rename = useCallback(async (id: string, title: string) => {
    await api.patch('/chat/threads/' + id, { title });
    queryClient?.invalidateQueries({ queryKey: ['chat-threads'] });
  }, [queryClient]);

  // --- stop ---
  const stop = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  // --- sendWithHistory (internal) ---
  // Core send logic that accepts an explicit prior history array.
  // send() passes current messages as prior; retry() passes the trimmed history.
  const sendWithHistory = useCallback(
    async (text: string, prior: ChatMessage[], context?: unknown) => {
      if (isSending) return;
      setError(null);
      setIsSending(true);

      const userMsg: ChatMessage = {
        id: nextId(),
        role: 'user',
        content: text,
        cards: [],
        created_at: nowSecs(),
      };
      const assistantId = nextId();
      const assistantMsg: ChatMessage = {
        id: assistantId,
        role: 'assistant',
        content: '',
        cards: [],
        created_at: nowSecs(),
      };

      setMessages((m) => [...m, userMsg, assistantMsg]);
      setStreamingText('');

      // Build context history from prior: drop empty-content entries, cap to last 20,
      // then append the new user message.
      const historyMessages = prior
        .filter((m) => m.content !== '')
        .slice(-HISTORY_CAP)
        .map((m) => ({ role: m.role, content: m.content }));
      historyMessages.push({ role: 'user', content: text });

      const controller = new AbortController();
      abortRef.current = controller;

      // Declare acc, cards and actions before try so catch can finalize assistant message on abort.
      let acc = '';
      const cards: ChatCard[] = [];
      const actions: string[] = [];
      const steps: TraceStep[] = [];
      let meta: { threadId: string; messageId: string; model: string; provider: string } | undefined;
      let doneInfo: { usage?: TurnTrace['usage']; ms?: number; grounded?: boolean; cappedOut?: boolean } = {};
      const startedAt = Date.now();

      /**
       * The trace as it stands right now. Called on the normal path AND on abort,
       * because a stopped turn still did whatever it did before being stopped and
       * hiding that would misreport it as having read nothing.
       *
       * `grounded` prefers the server's own answer and falls back to the steps when
       * `done` never arrived, which is exactly the abort case.
       */
      const buildTrace = (): TurnTrace | undefined => {
        if (!meta) return undefined;
        return {
          ...meta,
          iterations: steps.length,
          steps,
          usage: doneInfo.usage,
          ms: doneInfo.ms ?? Date.now() - startedAt,
          grounded: doneInfo.grounded ?? steps.some((s) => s.kind === 'read'),
          cappedOut: doneInfo.cappedOut ?? false,
        };
      };

      try {
        const res = await fetch(`${API_BASE}/chat`, {
          method: 'POST',
          signal: controller.signal,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken() ?? ''}`,
          },
          body: JSON.stringify({ threadId, messages: historyMessages, context, ...modelSelection() }),
        });

        if (!res.ok) {
          let message = 'The assistant is unavailable right now. Please try again.';
          try {
            const body = (await res.json()) as { error?: string };
            if (body?.error) message = body.error;
          } catch {
            /* non-JSON error body — keep the generic message */
          }
          throw new Error(message);
        }
        if (!res.body) throw new Error('The assistant returned no response. Please try again.');

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let hadError = false;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const chunks = buffer.split('\n\n');
          buffer = chunks.pop() ?? '';
          for (const chunk of chunks) {
            const dataLine = chunk.split('\n').find((l) => l.startsWith('data: '));
            if (!dataLine) continue;
            let evt: ChatSseEvent;
            try {
              evt = JSON.parse(dataLine.slice(6));
            } catch {
              continue;
            }
            if (evt.type === 'text') {
              acc += evt.delta;
              setStreamingText(acc);
            } else if (evt.type === 'card') {
              cards.push(evt.card);
            } else if (evt.type === 'tool') {
              actions.push(evt.label);
            } else if (evt.type === 'meta') {
              meta = { threadId: evt.threadId, messageId: evt.messageId, model: evt.model, provider: evt.provider };
              // Push it to the bar immediately. This frame arrives before any model
              // work, so the ids are on screen even for a turn that then fails.
              setTurnMeta({ ...meta, toolCount: 0, errorCount: 0 });
            } else if (evt.type === 'trace') {
              steps.push(evt.step);
              setTurnMeta((prev) => (prev ? {
                ...prev,
                toolCount: steps.length,
                errorCount: steps.filter((s) => s.error).length,
              } : prev));
            } else if (evt.type === 'done') {
              doneInfo = { usage: evt.usage, ms: evt.ms, grounded: evt.grounded, cappedOut: evt.cappedOut };
              setTurnMeta((prev) => (prev ? { ...prev, ...doneInfo } : prev));
            } else if (evt.type === 'canvas') {
              setCanvasSignal({ draftId: evt.draftId, revision: evt.revision });
            } else if (evt.type === 'error') {
              hadError = true;
              setError(evt.message);
              reader.cancel();
              break;
            }
          }
        }

        // Flush any remaining complete event left in buffer
        if (buffer.trim()) {
          const dataLine = buffer.split('\n').find((l) => l.startsWith('data: '));
          if (dataLine) {
            try {
              const evt = JSON.parse(dataLine.slice(6)) as ChatSseEvent;
              if (evt.type === 'text') { acc += evt.delta; }
              else if (evt.type === 'card') { cards.push(evt.card); }
              else if (evt.type === 'tool') { actions.push(evt.label); }
              else if (evt.type === 'meta') {
                meta = { threadId: evt.threadId, messageId: evt.messageId, model: evt.model, provider: evt.provider };
                setTurnMeta({ ...meta, toolCount: 0, errorCount: 0 });
              }
              else if (evt.type === 'trace') { steps.push(evt.step); }
              else if (evt.type === 'done') {
                doneInfo = { usage: evt.usage, ms: evt.ms, grounded: evt.grounded, cappedOut: evt.cappedOut };
                setTurnMeta((prev) => (prev ? { ...prev, ...doneInfo, toolCount: steps.length } : prev));
              }
              else if (evt.type === 'canvas') { setCanvasSignal({ draftId: evt.draftId, revision: evt.revision }); }
              else if (evt.type === 'error') { hadError = true; setError(evt.message); }
            } catch { /* ignore malformed trailing frame */ }
          }
        }

        // Only patch the assistant message with content when the stream completed without an SSE error.
        // On error, leave the error banner in place and clear streamingText without overwriting the bubble.
        if (!hadError) {
          const trace = buildTrace();
          setMessages((m) =>
            m.map((msg) =>
              msg.id === assistantId
                ? {
                    ...msg,
                    content: acc,
                    cards,
                    actions: actions.length > 0 ? actions : undefined,
                    ...(trace ? { trace } : {}),
                    created_at: nowSecs(),
                  }
                : msg,
            ),
          );
        }
        setStreamingText('');

        // Invalidate threads list so the history panel re-orders after a successful send.
        if (!hadError) {
          queryClient?.invalidateQueries({ queryKey: ['chat-threads'] });
        }
      } catch (e) {
        if (e instanceof DOMException && e.name === 'AbortError') {
          // Clean stop — finalize the assistant message with whatever streamed so far, clear streaming text.
          // isSending is cleared in finally; no error state set.
          // The trace comes along: a stopped turn still read whatever it read before
          // the stop, and dropping it would report the answer as ungrounded.
          const trace = buildTrace();
          setMessages((m) =>
            m.map((msg) =>
              msg.id === assistantId
                ? {
                    ...msg,
                    content: acc,
                    cards,
                    actions: actions.length > 0 ? actions : undefined,
                    ...(trace ? { trace } : {}),
                    created_at: nowSecs(),
                  }
                : msg,
            ),
          );
          setStreamingText('');
        } else {
          setError(e instanceof Error ? e.message : 'Chat failed');
        }
      } finally {
        setIsSending(false);
        abortRef.current = null;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [threadId, isSending, queryClient],
  );

  // --- send ---
  // Public API: passes the current messages snapshot as prior history.
  const send = useCallback(
    (text: string, context?: unknown) => sendWithHistory(text, messages, context),
    [sendWithHistory, messages],
  );

  // --- retry ---
  const retry = useCallback(() => {
    // Find the index of the last user message in the current messages array.
    const lastUserIdx = [...messages].reduceRight(
      (found, m, i) => (found === -1 && m.role === 'user' ? i : found),
      -1,
    );
    if (lastUserIdx === -1) return;

    const lastUser = messages[lastUserIdx];
    // Build trimmed prior: everything BEFORE the last user message
    // (drops the failed user turn and any trailing empty assistant placeholder).
    const trimmedPrior = messages.slice(0, lastUserIdx);

    // Re-send using the trimmed prior so the user message appears exactly once.
    return sendWithHistory(lastUser.content, trimmedPrior);
  }, [messages, sendWithHistory]);

  // --- regenerate ---
  // Re-run the last assistant reply from scratch.
  const regenerate = useCallback(() => {
    if (isSending) return;

    // Find the last assistant message index
    const lastAssistantIdx = [...messages].reduceRight(
      (found, m, i) => (found === -1 && m.role === 'assistant' ? i : found),
      -1,
    );
    if (lastAssistantIdx === -1) return;

    // Find the last user message before that assistant message
    const priorToAssistant = messages.slice(0, lastAssistantIdx);
    const lastUserIdx = [...priorToAssistant].reduceRight(
      (found, m, i) => (found === -1 && m.role === 'user' ? i : found),
      -1,
    );
    if (lastUserIdx === -1) return;

    const lastUserText = messages[lastUserIdx].content;
    // History = everything before that user message
    const priorBeforeThatUser = messages.slice(0, lastUserIdx);

    // Remove the assistant message (and anything after the user message) from state
    setMessages(messages.slice(0, lastUserIdx));

    return sendWithHistory(lastUserText, priorBeforeThatUser);
  }, [isSending, messages, sendWithHistory]);

  // --- editAndResend ---
  // Find the message with messageId (must be a user message), truncate messages to everything
  // BEFORE it, then send newText with that truncated history.
  const editAndResend = useCallback((messageId: string, newText: string) => {
    if (isSending) return;

    const idx = messages.findIndex((m) => m.id === messageId);
    if (idx === -1) return;
    const msg = messages[idx];
    if (msg.role !== 'user') return;

    const truncatedPrior = messages.slice(0, idx);
    setMessages(truncatedPrior);
    return sendWithHistory(newText, truncatedPrior);
  }, [isSending, messages, sendWithHistory]);

  // Refresh every active view after a sidebar write — see the note in confirm().
  const invalidateAfterWrite = useCallback(() => {
    queryClient?.invalidateQueries();
  }, [queryClient]);

  // --- confirm ---
  const confirm = useCallback(async (card: ChatCard): Promise<{ link?: string }> => {
    if (!card.commit) return {};
    const { method, path, body } = card.commit;
    let resp: Record<string, unknown> | undefined;
    if (method === 'POST') {
      resp = await api.post<Record<string, unknown>>(path, body ?? {});
    } else if (method === 'PATCH') {
      resp = await api.patch<Record<string, unknown>>(path, body ?? {});
    } else if (method === 'DELETE') {
      await api.delete(path);
    }
    const link = linkFor(card.entityType, resp?.id as string | undefined);

    // Append outcome feedback so the model knows the action is done on the next turn.
    const pastTense: Record<ChatCard['action'], string> = {
      create: 'Created',
      update: 'Updated',
      delete: 'Deleted',
      link: 'Linked',
      view: 'Viewed',
    };
    const outcomeLine = `✓ ${pastTense[card.action]} ${card.entityType}: ${card.title}${link ? ` (id saved, view ${link})` : ''}`;

    setMessages((prev) => {
      // Prefer appending to the owning assistant message (the one whose cards array contains this card).
      const ownerIdx = prev.findIndex((m) => m.role === 'assistant' && m.cards.includes(card));
      if (ownerIdx !== -1) {
        return prev.map((m, i) =>
          i === ownerIdx
            ? { ...m, content: m.content ? `${m.content}\n${outcomeLine}` : outcomeLine }
            : m,
        );
      }
      // Fallback: append a new assistant message.
      const outcomeMsg: ChatMessage = {
        id: `local-outcome-${card.title}-${Date.now()}`,
        role: 'assistant',
        content: outcomeLine,
        cards: [],
        created_at: nowSecs(),
      };
      return [...prev, outcomeMsg];
    });

    // The write landed on the server, so anything on screen showing that data is
    // now stale. Without this the page behind the sidebar keeps rendering the old
    // record and the write reads as having silently failed — a contact linked to
    // an opportunity from chat stayed invisible on the opportunity page, so it
    // got proposed and confirmed again, and the assistant eventually declared it
    // a system limitation. It had worked every time.
    //
    // Invalidating everything rather than a per-entity key map is deliberate: a
    // sidebar write can touch any entity, and a key map would drift out of date
    // silently. react-query only refetches ACTIVE queries, so the cost is bounded
    // by what the user is actually looking at.
    invalidateAfterWrite();

    return { link };
  }, [invalidateAfterWrite]);

  // --- runRowAction ---
  // Fire a display-card row's one-tap write (e.g. mark a task done). Reuses the
  // same commit path as confirm(); throws on failure so the caller can revert its
  // optimistic UI and surface an error.
  const runRowAction = useCallback(async (commit: CommitDescriptor): Promise<void> => {
    const { method, path, body } = commit;
    if (method === 'POST') await api.post(path, body ?? {});
    else if (method === 'PATCH') await api.patch(path, body ?? {});
    else if (method === 'DELETE') await api.delete(path);
    invalidateAfterWrite();
  }, [invalidateAfterWrite]);

  // --- canUndo ---
  // One trust model: any card carrying an explicit inverse op (update reverts
  // fields, delete/create carry their own) is undoable. Contact/talent creates
  // keep the legacy link-based path for backward compatibility.
  const canUndo = useCallback((card: ChatCard): boolean => {
    if (card.undo) return true;
    return (
      card.action === 'create' &&
      UNDOABLE_ENTITY_TYPES.has(card.entityType) &&
      !!card.link
    );
  }, []);

  // --- undo ---
  // Prefer the card's explicit inverse op (update → PATCH prior values).
  // Fall back to the scoped delete for just-created contact/talent records.
  const undo = useCallback(async (card: ChatCard): Promise<{ ok: boolean }> => {
    if (card.undo) {
      const { method, path, body } = card.undo;
      if (method === 'PATCH') await api.patch(path, body ?? {});
      else if (method === 'POST') await api.post(path, body ?? {});
      else if (method === 'DELETE') await api.delete(path);
      return { ok: true };
    }

    if (!canUndo(card)) return { ok: false };

    // Parse the entity id from card.link
    // /contacts/:id -> DELETE /parties/:id
    // /talent/:id   -> DELETE /talent/:id
    const link = card.link!;
    const parts = link.split('/').filter(Boolean);
    const entityId = parts[parts.length - 1];

    const deletePath =
      card.entityType === 'contact' ? `/parties/${entityId}` : `/talent/${entityId}`;

    await api.delete(deletePath);
    return { ok: true };
  }, [canUndo]);

  return {
    // existing
    messages,
    streamingText,
    isSending,
    error,
    send,
    confirm,
    // new
    threadId,
    newChat,
    loadThread,
    deleteThread,
    stop,
    retry,
    // task 2 additions
    regenerate,
    editAndResend,
    rename,
    undo,
    canUndo,
    // display-card inline actions
    runRowAction,
    // draft canvas: the main pane follows the conversation
    canvasSignal,
    // metrics bar
    turnMeta,
  };
}

export function useChatThreads() {
  return useQuery({
    queryKey: ['chat-threads'],
    queryFn: () =>
      api.get<{ threads: ChatThreadSummary[] }>('/chat/threads').then((r) => r.threads),
  });
}
